/** Клиент HTTP API сервера Chita. */

export interface Author {
  id: number
  name: string
  last_name?: string
  letter?: string
  books?: number
}

export interface Series {
  id: number
  name: string
  books?: number
}

export interface Genre {
  code: string
  name: string
  section: string
}

export interface BookSummary {
  id: number
  title: string
  authors: Author[]
  series: Series | null
  series_index: number | null
  lang: string
  /** Название языка для показа: таблицу кодов ведёт сервер. */
  lang_name?: string
  /** Язык не из метаданных, а определённый по тексту книги. */
  lang_guessed?: boolean
  year: number | null
  has_cover: boolean
  formats: string[]
  readable: boolean
  added_at: number
  updated_at: number
}

export interface BookFile {
  id: number
  format: string
  size: number
  has_text: boolean
  in_archive: boolean
}

export interface ReadingState {
  chapter: number
  block: number
  char_offset: number
  progress: number
  max_progress: number
  max_chapter?: number
  max_block?: number
  max_char_offset?: number
  client_updated_at: number
  device_id: string
}

/** Сведения, которые лежат внутри файла книги и дочитываются по требованию. */
export interface BookDetails {
  annotation: string
  translators: string[]
  keywords: string[]
  src_lang: string
  src_title: string
  src_authors: string[]
  publisher: string
  publish_city: string
  publish_year: number | null
  publish_title: string
  isbn: string
  size_bytes: number
  filled: boolean
}

export interface BookDetail extends BookSummary {
  annotation: string
  genres: Genre[]
  files: BookFile[]
  reading: ReadingState | null
  details: BookDetails
}

export interface AuthorCard extends Author {
  books: number
  series: Series[]
}

export interface ChapterInfo {
  index: number
  title: string
  level: number
  blocks: number
  chars: number
  start_char: number
}

export interface Manifest {
  version: number
  book_id: number
  title: string
  authors: string[]
  series: string
  lang: string
  total_chars: number
  total_blocks: number
  chapters: ChapterInfo[]
  images: string[]
}

/** Блок канонического формата. `s` — инлайн-разметка [начало, конец, стиль]. */
export interface Block {
  i: number
  t: string
  c?: string
  s?: [number, number, string][]
  src?: string
  lvl?: number
}

export interface Chapter {
  index: number
  title: string
  start_char: number
  blocks: Block[]
}

export interface Page<T> {
  items: T[]
  total: number
  limit: number
  offset: number
  /** Похожая фамилия, когда поиск не дал ничего. */
  suggestion?: string
}

export interface Position {
  book_id: number
  chapter: number
  block: number
  char_offset: number
  progress: number
  client_updated_at: number
}

export interface LetterCount {
  letter: string
  authors?: number
  series?: number
}

export interface GenreSection {
  section: string
  books: number
  genres: { code: string; name: string; books: number }[]
}

export type ShelfStatus = 'none' | 'reading' | 'finished' | 'want' | 'dropped'

/** Человеческие названия статусов — один список на весь клиент. */
export const SHELF_LABELS: Record<ShelfStatus, string> = {
  none: 'Без отметки',
  reading: 'Читаю',
  finished: 'Прочитано',
  want: 'Хочу прочесть',
  dropped: 'Отложено',
}

/** Книга на полке или в истории: к обычной карточке добавлены отметки. */
export interface ShelfBook extends BookSummary {
  status: ShelfStatus
  favorite: boolean
  progress: number
  max_progress: number
  read_at: number
}

export interface Bookmark {
  uuid: string
  book_id: number
  book_title?: string
  kind: string
  chapter: number
  block: number
  char_offset: number
  excerpt: string
  note: string
  color: string
  created_at: number
}

export interface RelatedGroup {
  title: string
  items: BookSummary[]
}

export interface DiskInfo {
  drive: string
  free_bytes: number
  total_bytes: number
  free_ratio: number
}

export interface Health {
  started_at: number
  uptime_seconds: number
  database_bytes: number
  disks: DiskInfo[]
  roots: { path: string; label: string; available: boolean }[]
  backup: { count: number; latest: string; latest_at: number; bytes: number }
  warnings: string[]
}

export interface ServerStatus {
  books: number
  files: number
  authors: number
  series: number
  scanning: boolean
  sync_rev: number
  last_scan: Record<string, unknown> | null
  health: Health
}

export interface Account {
  id: number
  username: string
  shared: boolean
  created_at: number
  devices: number
  books: number
  is_owner: boolean
}

export interface AppInfo {
  available: boolean
  name?: string
  size?: number
  updated_at?: number
  sha256?: string
  url?: string
}

/** Присланный через клиент файл: один файл или архив с несколькими книгами. */
export interface UploadEntry {
  name: string
  size: number
  files: number
  uploaded_at: number
  uploaded_by: string
  mine: boolean
  can_delete: boolean
  books: BookSummary[]
}

export interface UploadsInfo {
  enabled: boolean
  max_bytes: number
  /** Допустимые расширения, с точкой: «.fb2», «.epub», «.zip»… */
  formats: string[]
  items: UploadEntry[]
}

/** Итог приёма одного файла. */
export interface UploadResult {
  name: string
  size: number
  ok: boolean
  duplicate?: boolean
  books?: BookSummary[]
  warnings?: string[]
  error?: string
}

/** Языки библиотеки со счётчиками — для фильтра каталога. */
export interface LangCount {
  code: string
  name: string
  books: number
  /** Скольким из них язык не объявлен, а определён по тексту. */
  guessed: number
}

/** Условия отбора книг — общие для дерева и поиска. */
export interface BookFilter {
  q?: string
  author_id?: number
  series_id?: number
  genre?: string
  /** Коды языков через запятую; `unknown` — язык неизвестен. */
  lang?: string
  readable?: boolean
  sort?: string
  limit?: number
  offset?: number
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

const TOKEN_KEY = 'chita.token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

/** Запрос к API с токеном и разбором ошибок. Языковое приложение строит на
 *  нём свои методы, поэтому функция экспортируется. */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const token = getToken()
  // Кука работает для <img> и прямых ссылок, заголовок — для fetch из другого
  // источника в режиме разработки. Шлём оба, сервер примет любой.
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, { ...init, headers, credentials: 'include' })
  if (!response.ok) {
    let detail = response.statusText
    try {
      const body = await response.json()
      detail = body.detail ?? detail
    } catch {
      /* тело не JSON — оставляем статус */
    }
    throw new ApiError(response.status, detail)
  }
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

export const api = {
  authStatus: () => request<{ configured: boolean }>('/api/auth/status'),

  setup: (username: string, password: string) =>
    request<{ token: string; username: string; device_id: string }>('/api/auth/setup', {
      method: 'POST',
      body: JSON.stringify({ username, password, ...deviceInfo() }),
    }),

  login: (username: string, password: string) =>
    request<{ token: string; username: string; device_id: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, ...deviceInfo() }),
    }),

  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  me: () => request<{ id: number; username: string; device_id: string }>('/api/auth/me'),

  books: (params: BookFilter) => request<Page<BookSummary>>(`/api/books${query({ ...params })}`),

  book: (id: number) => request<BookDetail>(`/api/books/${id}`),

  /** Подробности сразу для нескольких книг — чтобы отличать издания в списке. */
  booksDetails: (ids: number[]) =>
    request<{ items: Record<string, BookDetails> }>('/api/books/details', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  authorLetters: () => request<{ items: LetterCount[] }>('/api/tree/authors/letters'),

  seriesLetters: () => request<{ items: LetterCount[] }>('/api/tree/series/letters'),

  genreTree: () => request<{ items: GenreSection[] }>('/api/tree/genres'),

  langTree: () => request<{ items: LangCount[] }>('/api/tree/langs'),

  authors: (params: { q?: string; letter?: string; limit?: number; offset?: number }) =>
    request<{ items: Author[]; total: number }>(`/api/authors${query({ ...params })}`),

  author: (id: number) => request<AuthorCard>(`/api/authors/${id}`),

  series: (params: { q?: string; letter?: string; limit?: number }) =>
    request<{ items: Series[] }>(`/api/series${query({ ...params })}`),

  manifest: (id: number) => request<Manifest>(`/api/books/${id}/manifest`),

  chapter: (id: number, index: number) => request<Chapter>(`/api/books/${id}/chapters/${index}`),

  status: () => request<ServerStatus>('/api/status'),

  shelf: (params: { status?: string; favorite?: boolean; limit?: number; offset?: number }) =>
    request<Page<ShelfBook>>(`/api/shelf${query({ ...params })}`),

  setShelf: (bookId: number, body: { status?: ShelfStatus; favorite?: boolean }) =>
    request<{ book_id: number; status: ShelfStatus; favorite: boolean }>(`/api/shelf/${bookId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  history: (params: { limit?: number; offset?: number } = {}) =>
    request<Page<ShelfBook>>(`/api/history${query({ ...params })}`),

  bookmarks: (params: { limit?: number; offset?: number } = {}) =>
    request<Page<Bookmark>>(`/api/bookmarks${query({ ...params })}`),

  bookBookmarks: (bookId: number) =>
    request<{ items: Bookmark[] }>(`/api/books/${bookId}/bookmarks`),

  addBookmark: (bookId: number, body: Partial<Bookmark>) =>
    request<{ uuid: string }>(`/api/books/${bookId}/bookmarks`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeBookmark: (markId: string) =>
    request<{ deleted: boolean }>(`/api/bookmarks/${markId}`, { method: 'DELETE' }),

  randomBook: (params: { genre?: string; author_id?: number; lang?: string } = {}) =>
    request<BookSummary>(`/api/books/random${query({ ...params })}`),

  related: (bookId: number) => request<{ groups: RelatedGroup[] }>(`/api/books/${bookId}/related`),

  rescan: () => request<{ started: boolean }>('/api/scan', { method: 'POST' }),

  appInfo: () => request<AppInfo>('/api/app/android'),

  users: () => request<{ items: Account[] }>('/api/users'),

  createUser: (username: string, password: string, shared: boolean) =>
    request<{ id: number; username: string; shared: boolean }>('/api/users', {
      method: 'POST',
      body: JSON.stringify({ username, password, shared }),
    }),

  deleteUser: (id: number) =>
    request<{ deleted: boolean }>(`/api/users/${id}`, { method: 'DELETE' }),

  uploads: () => request<UploadsInfo>('/api/uploads'),

  deleteUpload: (name: string) =>
    request<{ deleted: boolean; files: number }>(
      `/api/uploads/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    ),

  pushPosition: (position: Position) =>
    request<{ rev: number; stale: unknown[] }>('/api/sync/push', {
      method: 'POST',
      body: JSON.stringify({ positions: [position] }),
    }),
}

/**
 * Отправляет файл на сервер с отчётом о ходе передачи.
 *
 * fetch не умеет сообщать, сколько уже ушло, а книга в сотню мегабайт по
 * домашнему Wi-Fi идёт десятки секунд — без полосы человек решит, что всё
 * зависло. Поэтому здесь XMLHttpRequest.
 */
export function uploadBook(
  file: File,
  onProgress: (sent: number, total: number) => void,
): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const form = new FormData()
    form.append('files', file, file.name)
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/uploads')
    xhr.withCredentials = true
    const token = getToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total)
    }
    xhr.onerror = () => reject(new ApiError(0, 'нет связи с сервером'))
    xhr.onload = () => {
      let body: { items?: UploadResult[]; detail?: string } = {}
      try {
        body = JSON.parse(xhr.responseText)
      } catch {
        /* тело не JSON — ниже подставится статус */
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, body.detail ?? xhr.statusText))
        return
      }
      const item = body.items?.[0]
      if (!item) {
        reject(new ApiError(xhr.status, 'сервер не вернул результат'))
        return
      }
      resolve(item)
    }
    xhr.send(form)
  })
}

export function coverUrl(bookId: number): string {
  return `/api/books/${bookId}/cover`
}

export function imageUrl(bookId: number, name: string): string {
  return `/api/books/${bookId}/images/${encodeURIComponent(name)}`
}

export function downloadUrl(fileId: number): string {
  return `/api/files/${fileId}/download`
}

export function formatSize(bytes: number): string {
  if (!bytes) return ''
  // Единицы доходят до терабайт: тем же способом показывается место на дисках,
  // а там мегабайты давно перестали читаться.
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ']
  let value = bytes
  let step = 0
  while (value >= 1024 && step < units.length - 1) {
    value /= 1024
    step += 1
  }
  return `${step === 0 ? value : value.toFixed(1)} ${units[step]}`
}

function deviceInfo() {
  const key = 'chita.device'
  let id = localStorage.getItem(key)
  if (!id) {
    id = randomId()
    localStorage.setItem(key, id)
  }
  return { device_id: id, device_name: 'Браузер', platform: navigator.platform || 'web' }
}

/**
 * Случайный идентификатор устройства.
 *
 * `crypto.randomUUID` появился недавно и есть не везде: браузеры читалок на
 * старых движках его не знают, и вход падал с «is not a function». Это не
 * секретный ключ, а лишь метка устройства для синхронизации, поэтому запасные
 * варианты допустимы.
 */
function randomId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined
  if (c?.randomUUID) {
    return c.randomUUID()
  }
  if (c?.getRandomValues) {
    const bytes = new Uint8Array(16)
    c.getRandomValues(bytes)
    // Формат UUID v4, собранный вручную из случайных байт.
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  // Совсем старый движок без Web Crypto: метке устройства хватит и этого.
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
